import { getQualityOutputMetricLabel, getUnitOfMeasureLabel, type OutputPropertyValue } from 'api';
import { capitalizeString, Stack, Typography } from 'ui';

import { type QualityData, QualityDataInput } from '../../types';

type Props = {
  qualityData: QualityData[];
};

const getQualityDataItemLabel = (item: [string, OutputPropertyValue]) => {
  if (item[0] in QualityDataInput) {
    return QualityDataInput[item[0] as keyof typeof QualityDataInput];
  }
  return capitalizeString(item[0].replaceAll('_', ' '));
};

const getQualityDataItemValue = (item: [string, OutputPropertyValue]) => {
  const outputPropertyValue = item[1];
  const unitLabel = getUnitOfMeasureLabel(outputPropertyValue.unit);

  const value = '-';
  if (outputPropertyValue.min_value && outputPropertyValue.max_value) {
    return `${outputPropertyValue.min_value}${unitLabel} - ${outputPropertyValue.max_value}${unitLabel}`;
  }
  if (outputPropertyValue.min_value) {
    return `min: ${outputPropertyValue.min_value}${unitLabel}`;
  }
  if (outputPropertyValue.max_value) {
    return `max: ${outputPropertyValue.max_value}${unitLabel}`;
  }
  if (outputPropertyValue.value) {
    return `${outputPropertyValue.value}${unitLabel}`;
  }
  return `${value}${unitLabel}`;
};

const hasValue = (propertyValue: OutputPropertyValue): boolean => {
  return !!(propertyValue.value || propertyValue.min_value || propertyValue.max_value);
};

export const QualityDataList = ({ qualityData }: Props) => {
  const filteredQualityData = qualityData
    .map((qualityDataItem) => {
      const properties = Object.entries(qualityDataItem.properties).filter(([_, propertyValue]) =>
        hasValue(propertyValue)
      );
      return {
        properties,
        quality_output_metric: qualityDataItem.quality_output_metric,
      };
    })
    .filter((qualityDataItem) => qualityDataItem.properties.length > 0);

  return (
    <>
      <Typography fontWeight="bold">Quality Data</Typography>
      <Stack>
        {filteredQualityData.map((qualityDataItem) => (
          <Stack
            key={qualityDataItem.quality_output_metric}
            direction="row"
            gap={1}
            data-testid={`quality-data-item-${qualityDataItem.quality_output_metric}`}
          >
            <Typography key={`quality-data-${qualityDataItem.quality_output_metric}`} fontWeight="bold">
              {getQualityOutputMetricLabel(qualityDataItem.quality_output_metric)}:
            </Typography>
            <Stack gap={1}>
              {qualityDataItem.properties.map((value) => (
                <Typography key={`${qualityDataItem.quality_output_metric}-${value[0]}`}>
                  {getQualityDataItemLabel(value)} - <b>{getQualityDataItemValue(value)}</b>
                </Typography>
              ))}
            </Stack>
          </Stack>
        ))}
        {filteredQualityData.length === 0 ? (
          <Typography variant="body2">
            Data not available because demand signal doesn&apos;t have target materials
          </Typography>
        ) : null}
      </Stack>
    </>
  );
};
